// ==========================================================================
// Form select
// Author: Globalia
// Licensed under the WTFPL license
// ==========================================================================

import grDropdown from 'groots/buds/grDropdown/grDropdown';

import {
    toggleAttributeValue,
    removeAttributeValue,
    wrapAll
} from 'groots/utilities';

class GrFormSelect {
    constructor(selector, options) {
        this.pluginName = 'grFormSelect';

        this.default = {
            class: 'gr-select',
        };

        // Merge defaults with user's settings
        this.config = {
            ...this.default,
            ...options,
        };

        // Create global references
        this.selector = typeof selector === 'string' ? document.querySelector(selector) : selector;

        this.wrapper = document.createElement('div');
        this.styledSelect = document.createElement('div');
        this.list = document.createElement('ul');
        this.options = this.selector.options;

        this.init();
    }

    //  Methods
    // ----------------------------------------------------------------------

    init() {
        if (this.selector === null) {
            throw new Error(this.pluginName + ': fail - something wrong with your selector');
        } else {
            // ready
            this.ready = true;

            toggleAttributeValue(this.selector, 'gr-select', 'ready');

            if (groots.verbose) {
                console.log(this.pluginName + ': ready');
            }

            // binding
            var arr = [
                'handleClick',
                'handleSelectChange',
            ];

            for (var i = 0, len = arr.length; i < len; i++) {
                var method = arr[i];
                this[method] = this[method].bind(this);
            }

            // addEventListener
            this.list.addEventListener('click', this.handleClick);
            this.selector.addEventListener('change', this.handleSelectChange);

            // process
            toggleAttributeValue(this.selector, 'gr-select', 'processing');

            if (groots.verbose) {
                console.log(this.pluginName + ': processing');
            }

            this.build();
            this.populate();
            this.initDropdown();

            // end process
            removeAttributeValue(this.selector, 'gr-select', 'processing');

            if (groots.verbose) {
                console.log(this.pluginName + ': processed');
            }
        }
    }

    build() {
        if (groots.verbose) {
            console.log(this.pluginName + ': build');
        }

        // hide select
        this.selector.classList.add(this.config.class + '-hidden');

        // build wrapper
        var selector = [this.selector];
        this.wrapper.className = this.config.class;
        this.wrapper.setAttribute('gr-form', 'select-wrapper');
        this.wrapper.setAttribute('gr-dropdown', '');
        wrapAll(selector, this.wrapper);

        // build box
        this.styledSelect.className = this.config.class + '__box';
        this.styledSelect.setAttribute('gr-dropdown-trigger', '');
        this.wrapper.appendChild(this.styledSelect);

        // build list
        this.list.className = this.config.class + '__options';
        this.list.setAttribute('gr-dropdown-target', '');
        this.wrapper.appendChild(this.list);
    }

    unbuild() {
        if (groots.verbose) {
            console.log(this.pluginName + ' unbuild');
        }

        this.depopulate();
        this.destroy();
        this.grDropdown.destroy();

        // TODO: Find a better way to unwrap
        var wrapper = this.wrapper;
        var parent = wrapper.parentNode;

        while (wrapper.firstChild) {
            parent.insertBefore(wrapper.firstChild, wrapper);
        }

        var selectors = [this.wrapper, this.styledSelect, this.list];

        for (var i = 0; i < selectors.length; i++) {
            selectors[i].parentNode.removeChild(selectors[i]);
        }

        // show select
        this.selector.classList.remove(this.config.class + '-hidden');
    }

    populate() {
        if (groots.verbose) {
            console.log(this.pluginName + ': populate');
        }

        var selectedOption = this.options.selectedIndex;

        // populate box
        this.styledSelect.textContent = this.options[selectedOption].text;

        // populate list
        Array.prototype.forEach.call(this.options, function(el, i) {
            var item = document.createElement('li');
            item.textContent = el.text;
            item.setAttribute('rel', el.value);
            el.hasAttribute('disabled') ? item.setAttribute('disabled', '') : '';
            this.list.appendChild(item);
        }.bind(this));
    }

    depopulate() {
        if (groots.verbose) {
            console.log(this.pluginName + ' depopulate');
        }

        var selectors = [this.styledSelect, this.list];

        for (var i = 0; i < selectors.length; i++) {
            while (selectors[i].firstChild) {
                selectors[i].removeChild(selectors[i].firstChild);
            }
        }
    }

    initDropdown() {
        if (groots.verbose) {
            console.log(this.pluginName + ' initDropdown');
        }

        this.grDropdown = new grDropdown(this.wrapper);
    }

    handleClick(event) {
        if (groots.verbose) {
            console.log(this.pluginName + ' handleClick');
        }

        if (!event.target.hasAttribute('disabled')) {
            this.styledSelect.textContent = event.target.textContent;
            this.grDropdown.moveTarget();

            this.selector.value = event.target.getAttribute('rel');

            var changeEvent = new Event('change');
            this.selector.dispatchEvent(changeEvent);
        }
    }

    handleSelectChange(event) {
        if (groots.verbose) {
            console.log(this.pluginName + ' handleSelectChange');
        }
    }

    // Destroy - remove listeners to prevent from memory leak
    destroy() {
        if (groots.verbose) {
            console.log(this.pluginName + ': destroyed');
        }

        removeAttributeValue(this.selector, 'gr-select', 'ready');

        // removeEventListener
        this.list.removeEventListener('click', this.handleClick);
        this.selector.removeEventListener('change', this.handleSelectChange);
    }
}

export default GrFormSelect;

//  Auto-Instantiation
// ----------------------------------------------------------------------

(function() {
    const all = document.querySelectorAll('[gr-form] select');
    groots.plugins.grFormSelect = {};

    if (all.length) {
        groots.plugins.grFormSelect.instances = {};
        let index = -1;

        while (all[++index]) {
            groots.plugins.grFormSelect.instances[index] = new GrFormSelect(all[index]);
        }
    }
}());

